import { utils, showConfirm } from '../utils.js';
import { renderAdminChildren } from './children.js';

let URI_ROOT;

// ─── Module state ─────────────────────────────────────────────────────────────

let churches          = [];
let currentChurchId   = null;
let currentChurchRole = null;
let currentLevelId    = null;
let currentGroupId    = null;
let adminHistoryDepth = 0;

const levelCache    = new Map(); // church_id → levels[]
const groupCache    = new Map(); // level_id  → groups[]
const scheduleCache = new Map(); // group_id  → topicDays[]

// ─── Entry point ──────────────────────────────────────────────────────────────

export function initAdmin() {
    URI_ROOT = window.AppSettings.uriRoot;
    document.getElementById('adminSidebar').addEventListener('click', handleSidebarClick);
    document.getElementById('adminMain').addEventListener('click', handleMainClick);
    window.addEventListener('popstate', handlePopstate);
    function goBackAdmin(e) {
        e.preventDefault();
        history.go(-(adminHistoryDepth + 1));
    }
    document.querySelector('.admin-back-link')?.addEventListener('click', goBackAdmin);
    document.querySelector('#dashboardLink')?.addEventListener('click', goBackAdmin);
    history.replaceState({ admin: 'home' }, '');
    loadChurches();
}

// ─── History navigation ───────────────────────────────────────────────────────

function handlePopstate(event) {
    const s = event.state;
    if (!s?.admin) return;

    adminHistoryDepth = Math.max(0, adminHistoryDepth - 1);

    if (s.admin === 'home') {
        currentChurchId   = null;
        currentLevelId    = null;
        currentGroupId    = null;
        currentChurchRole = null;
        document.getElementById('adminSidebar').classList.remove('mob-hidden');
        document.getElementById('adminMain').classList.remove('mob-visible');
        document.getElementById('adminMain').innerHTML = '<p class="admin-select-prompt"><i class="bi bi-arrow-left-circle"></i> Select a church to get started.</p>';
        document.querySelectorAll('.church-item').forEach(el => el.classList.remove('is-active'));
        return;
    }

    if (s.admin === 'church') {
        currentChurchId   = s.churchId;
        currentLevelId    = null;
        currentGroupId    = null;
        const church      = churches.find(c => c.id === s.churchId);
        currentChurchRole = church?.role_id ?? null;
        document.getElementById('adminSidebar').classList.add('mob-hidden');
        document.getElementById('adminMain').classList.add('mob-visible');
        loadLevels(s.churchId);
        return;
    }

    if (s.admin === 'level') {
        currentChurchId   = s.churchId;
        currentLevelId    = s.levelId;
        currentGroupId    = null;
        const church      = churches.find(c => c.id === s.churchId);
        currentChurchRole = church?.role_id ?? null;
        loadGroups(s.levelId, s.churchId);
        return;
    }

    if (s.admin === 'schedule') {
        currentChurchId = s.churchId;
        currentLevelId  = s.levelId;
        currentGroupId  = s.groupId;
        loadSchedule(s.groupId, s.groupTitle);
    }

    if (s.admin === 'children') {
        currentChurchId = s.churchId;
        const church    = churches.find((c) => c.id === s.churchId);
        currentChurchRole = church?.role_id ?? null;
        loadChildrenView(s.churchId);
    }
}

// ─── Event handlers ───────────────────────────────────────────────────────────

function handleSidebarClick(e) {
    const item = e.target.closest('.church-item');
    if (!item) return;

    document.querySelectorAll('.church-item').forEach(el => el.classList.remove('is-active'));
    item.classList.add('is-active');

    currentChurchId   = parseInt(item.dataset.churchId, 10);
    currentLevelId    = null;
    currentGroupId    = null;
    const church      = churches.find(c => c.id === currentChurchId);
    currentChurchRole = church?.role_id ?? null;

    // Mobile: hide sidebar, show main
    document.getElementById('adminSidebar').classList.add('mob-hidden');
    document.getElementById('adminMain').classList.add('mob-visible');

    history.pushState({ admin: 'church', churchId: currentChurchId }, '', location.pathname);
    adminHistoryDepth++;
    loadLevels(currentChurchId);
}

function handleMainClick(e) {
    if (e.target.closest('#backToChurches') || e.target.closest('#backToChurch')) {
        history.back();
        return;
    }

    if (e.target.closest('.level-item')) {
        const item     = e.target.closest('.level-item');
        currentLevelId = parseInt(item.dataset.levelId, 10);
        currentGroupId = null;
        history.pushState({ admin: 'level', levelId: currentLevelId, churchId: currentChurchId }, '', location.pathname);
        adminHistoryDepth++;
        loadGroups(currentLevelId, currentChurchId);
        return;
    }

    if (e.target.closest('#backToLevels')) {
        history.back();
        return;
    }

    if (e.target.closest('.btn-schedule')) {
        const btn        = e.target.closest('.btn-schedule');
        currentGroupId   = parseInt(btn.dataset.groupId, 10);
        const groupTitle = btn.dataset.groupTitle;
        history.pushState({ admin: 'schedule', groupId: currentGroupId, groupTitle, levelId: currentLevelId, churchId: currentChurchId }, '', location.pathname);
        adminHistoryDepth++;
        loadSchedule(currentGroupId, groupTitle);
        return;
    }

    if (e.target.closest('#backToGroups')) {
        history.back();
        return;
    }

    if (e.target.closest('.btn-topic-action')) {
        handleCancelClick(e.target.closest('.btn-topic-action'));
    }

    if (e.target.closest('.btn-children')) {
        const btn = e.target.closest('.btn-children');
        handleChildrenClick(parseInt(btn.dataset.churchId, 10));
    }
}

// ─── Data loading (with cache) ────────────────────────────────────────────────

async function loadChurches() {
    const sidebar = document.getElementById('adminSidebar');
    sidebar.innerHTML = '<div class="admin-sidebar-title"><i class="bi bi-building"></i> Churches</div><p class="admin-loading">Loading...</p>';

    try {
        const res = await fetch(`${URI_ROOT}ajax/admin/get_churches`);
        churches  = await res.json();
        renderChurches(sidebar);
    } catch {
        sidebar.innerHTML = '<div class="admin-sidebar-title"><i class="bi bi-building"></i> Churches</div><p class="admin-error">Failed to load.</p>';
    }
}

async function loadLevels(churchId) {
    const main = document.getElementById('adminMain');

    if (levelCache.has(churchId)) {
        renderChurchView(main, levelCache.get(churchId));
        return;
    }

    main.innerHTML = '<p class="admin-loading">Loading...</p>';
    try {
        const res    = await fetch(`${URI_ROOT}ajax/admin/get_levels?church_id=${churchId}`);
        const levels = await res.json();
        levelCache.set(churchId, levels);
        renderChurchView(main, levels);
    } catch {
        main.innerHTML = '<p class="admin-error">Failed to load.</p>';
    }
}

async function loadGroups(levelId, churchId) {
    const main = document.getElementById('adminMain');

    if (groupCache.has(levelId)) {
        renderGroupView(main, groupCache.get(levelId), levelId);
        return;
    }

    main.innerHTML = '<p class="admin-loading">Loading...</p>';
    try {
        const res    = await fetch(`${URI_ROOT}ajax/admin/get_groups?level_id=${levelId}&church_id=${churchId}`);
        const groups = await res.json();
        groupCache.set(levelId, groups);
        renderGroupView(main, groups, levelId);
    } catch {
        main.innerHTML = '<p class="admin-error">Failed to load.</p>';
    }
}

async function loadSchedule(groupId, groupTitle) {
    const main = document.getElementById('adminMain');

    if (scheduleCache.has(groupId)) {
        renderScheduleView(main, scheduleCache.get(groupId), groupTitle);
        return;
    }

    main.innerHTML = '<p class="admin-loading">Loading...</p>';
    try {
        const res      = await fetch(`${URI_ROOT}ajax/admin/get_schedule?group_id=${groupId}&church_id=${currentChurchId}`);
        const schedule = await res.json();
        scheduleCache.set(groupId, schedule);
        renderScheduleView(main, schedule, groupTitle);
    } catch {
        main.innerHTML = '<p class="admin-error">Failed to load.</p>';
    }
}

// ─── Renderers ────────────────────────────────────────────────────────────────

function renderChurches(sidebar) {
    if (!churches.length) {
        sidebar.innerHTML = '<p class="admin-empty">No churches assigned.</p>';
        return;
    }

    sidebar.innerHTML = `
        <div class="admin-sidebar-title"><i class="bi bi-building"></i> Churches</div>
        <ul class="church-list">
            ${churches.map(c => `
                <li class="church-item" data-church-id="${c.id}">
                    <span class="church-name">${c.name}</span>
                    <span class="church-role">${c.role_label}</span>
                </li>
            `).join('')}
        </ul>
    `;
}

function renderChurchView(main, levels) {
    const church  = churches.find(c => c.id === currentChurchId);
    const isAdmin = isAdminRole(currentChurchRole);

    main.innerHTML = `
        ${navRow('backToChurches', church?.name ?? '')}

        ${isAdmin ? `
        <div class="admin-section">
            <p class="admin-section-title">Church management</p>
            <div class="admin-actions-grid">
                ${actionCard('bi-people', 'Children', false, 'btn-children', `data-church-id="${currentChurchId}"`)}
                ${actionCard('bi-person-badge', 'Teachers', true)}
                ${actionCard('bi-layers', 'New Level', true)}
            </div>
        </div>` : ''}

        <div class="admin-section">
            <p class="admin-section-title">Levels</p>
            ${levels.length
                ? `<ul class="admin-list">
                    ${levels.map(l => `
                        <li class="level-item admin-list-item" data-level-id="${l.id}" data-level-label="${l.label}">
                            <span>${l.label}</span>
                            <i class="bi bi-chevron-right"></i>
                        </li>`).join('')}
                   </ul>`
                : '<p class="admin-empty">No levels in this church.</p>'}
        </div>
    `;
}

function renderGroupView(main, groups, levelId) {
    const church  = churches.find(c => c.id === currentChurchId);
    const level   = (levelCache.get(currentChurchId) ?? []).find(l => l.id === levelId);
    const isAdmin = isAdminRole(currentChurchRole);

    main.innerHTML = `
        ${navRow('backToLevels', level?.label ?? '', church?.name ?? '')}

        ${isAdmin ? `
        <div class="admin-section">
            <p class="admin-section-title">Level management</p>
            <div class="admin-actions-grid">
                ${actionCard('bi-person-plus', 'Add Group', true)}
                ${actionCard('bi-book', 'Topics', true)}
                ${actionCard('bi-bar-chart', 'Statistics', true)}
            </div>
        </div>` : ''}

        <div class="admin-section">
            <p class="admin-section-title">Groups</p>
            ${groups.length
                ? `<ul class="admin-list">
                    ${groups.map(g => renderGroupItem(g, level?.label ?? '')).join('')}
                   </ul>`
                : '<p class="admin-empty">No groups found.</p>'}
        </div>
    `;
}

function renderGroupItem(group, levelLabel) {
    const isAdmin   = isAdminRole(currentChurchRole);
    const statusMap = {
        opened: 'Opened', ready_to_close: 'Ready to close',
        closed: 'Closed', created: 'Created', configured: 'Configured',
    };

    return `
        <li class="admin-list-item group-item">
            <div class="group-info">
                <span class="group-title">${group.title}</span>
                <span class="group-status status-${group.status}">${statusMap[group.status] ?? group.status}</span>
            </div>
            <div class="group-actions">
                <button class="admin-action-btn btn-schedule"
                    data-group-id="${group.id}"
                    data-group-title="${group.title}"
                    data-level-label="${levelLabel}">
                    <i class="bi bi-calendar-x"></i> Schedule
                </button>
                ${isAdmin ? `
                <button class="admin-action-btn" disabled title="Coming soon">
                    <i class="bi bi-people"></i> Children
                </button>
                <button class="admin-action-btn" disabled title="Coming soon">
                    <i class="bi bi-person-badge"></i> Teachers
                </button>` : ''}
            </div>
        </li>
    `;
}

function renderScheduleView(main, topicDays, groupTitle) {
    const canRestore = isAdminRole(currentChurchRole);
    const level      = (levelCache.get(currentChurchId) ?? []).find(l => l.id === currentLevelId);

    main.innerHTML = `
        ${navRow('backToGroups', `${groupTitle} — Schedule`, level?.label ?? '')}

        <div class="admin-section">
            <p class="admin-section-title">Sessions available for management</p>
            ${topicDays.length
                ? `<div class="schedule-list">
                    ${topicDays.map(td => renderScheduleRow(td, canRestore)).join('')}
                   </div>`
                : '<p class="admin-empty">All sessions have attendance recorded — nothing to manage here.</p>'}
        </div>
    `;
}

function renderScheduleRow(td, canRestore) {
    const canceled   = td.canceled == 1;
    const badgeClass = canceled ? 'badge-canceled' : 'badge-active';
    const badgeLabel = canceled ? 'Canceled' : 'Active';

    let actionHtml = '';
    if (!canceled) {
        actionHtml = `<button class="btn-topic-action btn-cancel" data-id="${td.id}" data-canceled="0">
            <i class="bi bi-x-circle"></i> Cancel
        </button>`;
    } else if (canRestore) {
        actionHtml = `<button class="btn-topic-action btn-restore" data-id="${td.id}" data-canceled="1">
            <i class="bi bi-arrow-counterclockwise"></i> Restore
        </button>`;
    }

    return `
        <div class="schedule-row" data-topic-day-id="${td.id}">
            <span class="topic-date">${utils.formatDate(td.date)}</span>
            <span class="topic-title">${td.topic_title}</span>
            <span class="topic-badge ${badgeClass}">${badgeLabel}</span>
            ${actionHtml}
        </div>
    `;
}

// ─── UI helpers ───────────────────────────────────────────────────────────────

function navRow(backId, current, parent = '') {
    return `
        <div class="admin-nav-row">
            <button class="admin-back-btn" id="${backId}">
                <i class="bi bi-arrow-left"></i>
                <span class="mob-only">${parent || 'Back'}</span>
            </button>
            ${parent ? `<span class="admin-breadcrumb">${parent}</span>
            <i class="bi bi-chevron-right admin-sep"></i>` : ''}
            <span class="admin-breadcrumb is-current">${current}</span>
        </div>
    `;
}

function actionCard(icon, label, comingSoon = false, extraClass = '', extraData = '') {
    return `
        <button class="admin-action-card ${extraClass}" ${comingSoon ? 'disabled' : ''} ${extraData}>
            <i class="bi ${icon}"></i>
            <span>${label}</span>
            ${comingSoon ? '<small>Coming soon</small>' : ''}
        </button>
    `;
}

function isAdminRole(role) {
    return role === 'SUPER_ADMIN' || role === 'ADMIN';
}

// ─── Children section ─────────────────────────────────────────────────────────

function handleChildrenClick(churchId) {
    history.pushState({ admin: 'children', churchId }, '', location.pathname);
    adminHistoryDepth++;
    loadChildrenView(churchId);
}

function loadChildrenView(churchId) {
    const main   = document.getElementById('adminMain');
    const church = churches.find((c) => c.id === churchId);
    main.innerHTML = `
        ${navRow('backToChurch', `Children — ${church?.name ?? ''}`)}
        <div class="admin-section">
            <div id="childrenContainer"></div>
        </div>`;

    renderAdminChildren(main.querySelector('#childrenContainer'), churchId, URI_ROOT);
}

// ─── Cancel / Restore ─────────────────────────────────────────────────────────

async function handleCancelClick(btn) {
    const topicDayId  = parseInt(btn.dataset.id, 10);
    const wasCanceled = btn.dataset.canceled === '1';
    const willCancel  = !wasCanceled;

    const church      = churches.find(c => c.id === currentChurchId);
    const canRestore  = isAdminRole(currentChurchRole);
    const adminLabel  = church?.admin_role_label ?? 'administrator';

    const cancelMessage = canRestore
        ? 'Are you sure you want to cancel this session? You can restore it later if needed.'
        : `Are you sure? Only a ${adminLabel} can restore this session.`;

    const confirmed = await showConfirm({
        title:       willCancel ? 'Cancel session?' : 'Restore session?',
        message:     willCancel ? cancelMessage : 'This session will be restored as active.',
        confirmText: willCancel ? 'Cancel session' : 'Restore',
        iconClass:   willCancel ? 'bi-x-circle' : 'bi-arrow-counterclockwise',
    });

    if (!confirmed) return;
    btn.disabled = true;

    try {
        const res    = await fetch(`${URI_ROOT}ajax/admin/cancel_topic_day`, {
            method:  'POST',
            headers: { 'Content-Type': 'application/json' },
            body:    JSON.stringify({ topic_day_id: topicDayId, canceled: willCancel }),
        });
        const result = await res.json();

        if (result.success) {
            const cached = scheduleCache.get(currentGroupId);
            if (cached) {
                const td = cached.find(t => t.id === topicDayId);
                if (td) td.canceled = willCancel ? 1 : 0;
            }
            updateScheduleRow(btn, willCancel);
        }
    } catch {
        btn.disabled = false;
    }
}

function updateScheduleRow(btn, canceled) {
    const row = btn.closest('.schedule-row');
    if (!row) return;

    const badge       = row.querySelector('.topic-badge');
    badge.className   = `topic-badge ${canceled ? 'badge-canceled' : 'badge-active'}`;
    badge.textContent = canceled ? 'Canceled' : 'Active';

    if (canceled) {
        if (isAdminRole(currentChurchRole)) {
            btn.className        = 'btn-topic-action btn-restore';
            btn.innerHTML        = '<i class="bi bi-arrow-counterclockwise"></i> Restore';
            btn.dataset.canceled = '1';
            btn.disabled         = false;
        } else {
            btn.remove(); // TEACHER cannot restore
        }
    } else {
        btn.className        = 'btn-topic-action btn-cancel';
        btn.innerHTML        = '<i class="bi bi-x-circle"></i> Cancel';
        btn.dataset.canceled = '0';
        btn.disabled         = false;
    }
}
